import * as React from 'react';
import styled from 'styled-components';

import { color } from '@creditornot/cb-ingredients/design-tokens';

import { DataTableContext } from './DataTableContext';
import { DataTableRow } from './DataTableRow';
import { ColumnType, TRowModel, TableRowAction } from './types';
import { getSubRowSelectionStatus, groupBy } from './utils';

type Props<Row extends TRowModel<Row>, ParentRow extends TRowModel<Row>> = {
  className?: string;
  dataSource: Row[];
  getRowMenu?: (record: Row) => TableRowAction<Row>[];
  columns: ColumnType<Row>[];
  parentRow: ParentRow;
  /** Function to group the data by a key. */
  groupByKey?: (record: Row) => string;
  hideSelection?: boolean;
};

const GroupHeader = styled.div<{ $isParentSelected: boolean }>`
  padding: 12px 8px;
  grid-column: 1 / -1;
  display: grid;
  padding-left: 24px;
  border-bottom: 1px solid ${color.border};
  background-color: ${({ $isParentSelected }) =>
    $isParentSelected ? color.bgSurfaceBrandSelected : color.bgSurface};
`;

const Group = styled.div`
  @supports (grid-template-rows: subgrid) {
    display: grid;
    grid: inherit;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  }

  & + & {
    border-top: 1px solid ${color.border};
  }

  @supports not (grid-template-rows: subgrid) {
    display: contents;
  }
`;

export const DataTableSubRows = <Row extends TRowModel<Row>, ParentRow extends TRowModel<Row>>({
  dataSource = [],
  columns: defaultColumns,
  parentRow,
  getRowMenu,
  groupByKey,
  hideSelection = false,
}: Props<Row, ParentRow>) => {
  const { selectionState } = React.useContext(DataTableContext);

  const columns = defaultColumns.filter((column) => !column.hidden);
  const subRowSelectionStatus = getSubRowSelectionStatus(selectionState, parentRow);
  const isParentSelected = subRowSelectionStatus === 'ALL' || subRowSelectionStatus === 'SOME';

  const groupedData = groupByKey ? groupBy(dataSource, groupByKey) : { ungrouped: dataSource };

  return (
    <>
      {Object.keys(groupedData).map((groupKey) => {
        const records = groupedData[groupKey] ?? [];
        const isGrouped = groupKey && groupKey !== 'ungrouped';
        const Component = isGrouped ? Group : React.Fragment;

        return (
          <Component key={groupKey}>
            {isGrouped && (
              <GroupHeader $isParentSelected={isParentSelected} data-test-id="group-header">
                {groupKey}
              </GroupHeader>
            )}
            {records.map((record, rowIndex) => (
              <DataTableRow
                key={`${record.id}-${rowIndex}`}
                record={record}
                rowIndex={rowIndex}
                columns={columns}
                getRowMenu={getRowMenu}
                parentRow={parentRow as unknown as Row}
                isLastSubRow={rowIndex === records.length - 1}
                hideSelection={hideSelection}
              />
            ))}
          </Component>
        );
      })}
    </>
  );
};
